import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { ErrorState, LoadingState } from '../../components/ui/Feedback';
import { useHospitalTitle } from '../../lib/hospital-brand';
import { useDocumentHeaderLogo } from '../settings/settings.queries';
import { useIpdDaycareAdmission } from './ipd-daycare.queries';
import { formatIpdDateTime } from './ipd-daycare.utils';

const ADMISSION_FORM_HEADER_NAME = 'opd_prescription';

const upper = (value: string | number | null | undefined, fallback = '-') => {
  if (value === null || value === undefined || value === '') return fallback;
  return String(value).toUpperCase();
};

export const IpdAdmissionFormPrintPage = () => {
  const { admissionId } = useParams();
  const [params] = useSearchParams();
  const withHeader = params.get('header') === '1';
  const hospitalTitle = useHospitalTitle();
  const admissionQuery = useIpdDaycareAdmission(admissionId);
  const headerLogoQuery = useDocumentHeaderLogo(ADMISSION_FORM_HEADER_NAME, withHeader);

  useEffect(() => {
    if (admissionQuery.data && (!withHeader || headerLogoQuery.isFetched)) window.setTimeout(() => window.print(), 300);
  }, [admissionQuery.data, withHeader, headerLogoQuery.isFetched]);

  if (admissionQuery.isLoading) return <LoadingState />;
  if (admissionQuery.isError || !admissionQuery.data) return <ErrorState title="Admission not found" message="The admission could not be loaded." />;

  const admission = admissionQuery.data;
  const patient = admission.patientSnapshot;
  const responsible = admission.responsiblePerson;
  const address = upper([patient.address, patient.district, patient.state, patient.pinCode].filter(Boolean).join(', '));
  const age = `${patient.ageYears}Y ${patient.ageMonths}M ${patient.ageDays}D`;
  const headerLogo = withHeader ? headerLogoQuery.data : null;
  const sectionLabel = admission.admissionType === 'Daycare' ? 'DAYCARE' : 'IPD';

  return (
    <div className="print-admission-form">
      {withHeader ? (
        headerLogo ? (
          <div className="print-admission-form-logo"><img src={headerLogo} alt={hospitalTitle} /></div>
        ) : (
          <div className="print-admission-form-title-fallback">{hospitalTitle}</div>
        )
      ) : null}

      <h1 className="print-admission-form-heading">{admission.admissionType.toUpperCase()} ADMISSION FORM</h1>

      <table className="print-admission-form-infobar">
        <tbody>
          <tr>
            <td><b>UHID No. : {upper(patient.uhid)}</b></td>
            <td rowSpan={2} className="print-admission-form-barcode-cell">
              <span className="print-admission-form-barcode" aria-hidden="true" />
            </td>
            <td><b>Admission Date : {formatIpdDateTime(admission.admissionDate)}</b></td>
          </tr>
          <tr>
            <td><b>{sectionLabel} No. : {admission.ipdNumber}</b></td>
            <td><b>Patient Type :</b> {upper(admission.insuranceType)}, <b>No :</b> {upper(admission.insuranceNumber)}</td>
          </tr>
        </tbody>
      </table>

      <table className="print-admission-form-details">
        <thead>
          <tr>
            <th>PATIENT DETAILS</th>
            <th>PATIENT ADMISSION INFORMATION</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <b>Patient&apos;s Name : </b>{upper(patient.name)}<br />
              <b>Mobile No : </b>{upper(patient.phone)}<br />
              <b>Sex : </b>{upper(patient.gender)}, <b>Age : </b>{age}<br />
              {patient.guardianName ? <><b>Guardian Name : </b>{upper(patient.guardianName)}<br /></> : null}
              {patient.relation ? <><b>Guardian Relation : </b>{upper(patient.relation)}<br /></> : null}
              <b>Address : </b>{address}<br />
              <b>Aadhar Card No : </b>{upper(patient.aadhaar)}
            </td>
            <td>
              <b>Package Type : </b>{upper(admission.packageType)}<br />
              <b>Bed : </b>{upper(admission.bed.name)}, <b>Ward : </b>{upper(admission.bedCategory.name)}<br />
              <b>Under Doctor : </b>{admission.underDoctor ? `DR. ${upper(admission.underDoctor.name)}` : '-'}<br />
              {admission.otherUnderDoctor ? <><b>Others Under Doctor : </b>DR. {upper(admission.otherUnderDoctor.name)}<br /></> : null}
              {admission.diagnosisAtAdmission ? <><b>Admission Time Diagnosis : </b><span dangerouslySetInnerHTML={{ __html: admission.diagnosisAtAdmission }} /><br /></> : null}
              <b>Patient Type : </b>{upper(admission.insuranceType)}
            </td>
          </tr>
        </tbody>
      </table>

      <h2 className="print-admission-form-consent-heading">CONSENT</h2>
      <p className="print-admission-form-consent">
        1. I, <b>{(responsible.name ?? '').toUpperCase()}</b> hereby acknowledge that I have incurred all charges mentioned in this form upon
        admission to {hospitalTitle} Hospital And Diagnostic Service. I fully understand and agree to the payment terms specified by the hospital,
        from the time of admission to discharge. I pledge to settle all outstanding bills, including any charges incurred during my stay, prior to
        discharge.
        <br />
        2. I am aware of and fully comprehend the rules and regulations regarding patient discharge as outlined by the hospital. Specifically:
        (2.1) If I am unable to escort the patient by 11 AM on the day of discharge, I commit to making the full payment for that day.
        <br />
        (2.2) I promise that no undue pressure will be created by me to the hospital staff for the patient&apos;s discharge. The hospital has
        explained that certain tasks, such as creating the MRD file, preparing a discharge summary, and ensuring the patient is ready for
        departure, may take time. Completing all these tasks simultaneously may cause delays in the patient&apos;s discharge.
        <br />
        (2.3) I acknowledge that visitors, including family members or others, are not allowed to stay with the patient in the ward. Furthermore,
        I understand that I cannot visit the patient during visiting hours without presenting the hospital&apos;s visiting card to the hospital
        authorities or security.
        <br />
        3. I understand that the hospital will not be responsible for any loss of valuable belongings, jewelry, or cash belonging to the patient
        or their relatives. I will personally ensure the safekeeping of all such items.
        <br />
        4. I consent to the hospital&apos;s requirement for various blood tests and other necessary tests for the patient&apos;s medical treatment
        from the time of admission. I authorize the hospital to conduct all required tests.
        <br />
        5. In the event of a referral to another hospital for medical reasons, I understand that I will be responsible for settling the entire bill
        before transferring the patient.
        <br />
        6. The doctors and staff at the hospital have explained to me about the critical condition of the patient. They have also stated that in
        the event of any deterioration in the patient&apos;s physical condition or death for any reason, the hospital authorities will not hold
        any responsibility.
        <br />
        I have heard and understood all of this and admitting the patient with the understanding that I will not hold the hospital staff
        responsible in any way if there is a decline in the patient&apos;s physical condition or in the unfortunate event of death.
        <br />
        I hereby provide my consent to the terms and conditions mentioned above and affirm my commitment to fulfilling all financial obligations
        related to the patient&apos;s admission and treatment.
      </p>

      <table className="print-admission-form-responsible">
        <tbody>
          <tr>
            <td>
              <span className="print-admission-form-label">Name</span>
              <span className="print-admission-form-blank print-admission-form-blank-name">{upper(responsible.name, '')}</span>
              <span className="print-admission-form-label">Age</span>
              <span className="print-admission-form-blank print-admission-form-blank-age">{upper(responsible.age, '')}</span>
              <span className="print-admission-form-label">Contact No</span>
              <span className="print-admission-form-blank print-admission-form-blank-phone">{upper(responsible.phone, '')}</span>
            </td>
          </tr>
          <tr>
            <td>
              <span className="print-admission-form-label">Address</span>
              <span className="print-admission-form-blank print-admission-form-blank-address">{upper(responsible.address, '')}</span>
              <span className="print-admission-form-label">Relation With Patient</span>
              <span className="print-admission-form-blank print-admission-form-blank-relation">{upper(responsible.relation, '')}</span>
            </td>
          </tr>
          <tr>
            <td className="print-admission-form-guardian-signature">Guardian Signature</td>
          </tr>
        </tbody>
      </table>

      <div className="print-admission-form-signoff">
        <span>Next of Kin/Accompanying person Signature &amp; Date</span>
        <span>Admitting Officer Signature &amp; Date</span>
      </div>
    </div>
  );
};
